import { push } from 'connected-react-router';
import { Dispatch } from 'redux';
import { commonHandleShowErrorMessage } from 'src/actions/common';
import { begin, end } from 'src/actions/indicator';
import * as LoginActions from '../actions/login';
import * as MessageActions from '../actions/message';
import * as RootActions from '../actions/root';
import * as SessionActions from '../actions/session';
import i18n from '../resources/i18n';
import Config from '../site.config.json';
import { fetchCMData } from '../utils/CMData';
import { fetchSystemMst, MasterItemCode } from '../utils/MasterData';
import ReplaceStyleKeywords from '../utils/ReplaceStyleKeywords';
import { Routes, Screens } from '../utils/Routes';
import Transfers, {
	IPwrErrorResponse,
	ITsqErrorResponse,
} from '../utils/Transfers';

// short cut
const showProgress = () =>
	LoginActions.showLoginProgress({
		isShowProgress: true,
	});
const hideProgress = () =>
	LoginActions.showLoginProgress({
		isShowProgress: false,
	});

/**
 * パスワードリマインダのパスワード変更初期表示処理
 * @param dispatch ディスパッチャ
 * @param authKey 認証キー
 * @param companyCode 法人コード
 */
export const pwrPasswordChangeInit = (
	dispatch: Dispatch,
	authKey: string,
	companyCode?: string
) => {
	dispatch(showProgress());
	Transfers.fetchChangePasswordInit(authKey, companyCode)
		.then(data => {
			// 成功の場合、パスワード変更のログインIDに設定します。
			const loginID = data.loginID;
			dispatch(LoginActions.setLoginIDPwrChangePassword({ loginID }));
		})
		.catch((e: IPwrErrorResponse) => {
			if (e.result === 'failed') {
				dispatch(
					commonHandleShowErrorMessage({
						content: e.messageKey,
						replacements: [],
						transitionTo: Routes.getPath(Screens.LOGIN),
					})
				);
			} else {
				dispatch(
					MessageActions.showMessage({
						messages: [
							{
								content: i18n.t('login_Scenario_0001'),
							},
						],
						title: i18n.t('login_Scenario_0002'),
					})
				);
			}
		})
		.finally(() => {
			dispatch(hideProgress());
		});
};

/**
 * メッセージ読み込み
 * @param dispatch ディスパッチャ
 * @param companyCode 法人コード
 */
export const fetchMessages = (
	dispatch: Dispatch,
	companyCode: string | undefined
) => {
	const key = 'loginInit.action';
	dispatch(begin({ key }));
	Transfers.fetchManagerNoticeMessages(companyCode)
		.then(data => {
			// お知らせの画面表示
			const carouselChangeInterval = +data.carouselChangeInterval;

			const {
				identificationItems,
				loginIdLinkEnabled,
				passwordLinkEnabled,
			} = data.pwr;
			const noticeMessages = data.lmcmDisplayManagerNoticeMessages as LoginActions.INoticeMessage[];
			const messages = noticeMessages.map(msgMap => {
				return {
					contact: msgMap.contact,
					message: msgMap.message.map(msg => {
						msg = ReplaceStyleKeywords.replaceStyleKeywords(msg);
						return msg;
					}),
					startDate: msgMap.startDate,
					titleList: msgMap.titleList.map(msg => {
						msg = ReplaceStyleKeywords.replaceStyleKeywords(msg);
						return msg;
					}),
				};
			});
			const showCMOption = data.showCMOption;
			const showLangOption = data.showLangOption;
			dispatch(
				LoginActions.setLoginManagerNotice({
					carouselChangeInterval,
					messages,
					pwr: {
						identificationItems,
						loginIdLinkEnabled,
						passwordLinkEnabled,
					},
				})
			);

			// ロゴ設定
			dispatch(
				LoginActions.setLoginLogo({
					showCMOption,
					showLangOption,
				})
			);
		})
		.catch((e: ITsqErrorResponse) => {
			// FIXME: ちゃんとLoggerを設定して吐きたい
			// tslint:disable-next-line
			console.error(e);
			dispatch(
				commonHandleShowErrorMessage({
					content: e.messageKey,
					replacements: [],
				})
			);
		})
		.finally(() => {
			dispatch(end({ key }));
		});
};

/**
 * ログイン処理
 * @param dispatch ディスパッチャ
 * @param companyCode テナントコード
 * @param loginID ログインID
 * @param password パスワード
 */
export const login = (
	dispatch: Dispatch,
	companyCode: string,
	loginID: string,
	password: string
) => {
	dispatch(showProgress());
	Transfers.login(companyCode, loginID, password)
		.then(data => {
			if (data.result !== 'success') {
				dispatch(hideProgress());
				dispatch(
					MessageActions.showMessage({
						messages: [
							{
								content: data.messageKey,
							},
						],
						title: data.titleKey,
					})
				);
			} else {
				if (data.userInfo.needChangePassword) {
					// パスワード変更画面に遷移
					dispatch(hideProgress());
					dispatch(
						MessageActions.showMessage({
							messages: [
								{
									content: data.messageKey,
								},
							],
							title: data.titleKey,
						})
					);
					const to = Routes.getPath(Screens.CHANGE_PASSWORD);
					const pushState = {
						companyCode,
						loginID,
						managerChk: data.userInfo.managerChk,
					};
					dispatch(push(to, pushState));
				} else {
					// ユーザデータを取得し、お知らせ画面に遷移
					dispatch(hideProgress());
					getUserInfo(dispatch, loginID, data.userInfo.managerChk);
				}
			}
		})
		.catch(e => {
			// handle network error
			dispatch(hideProgress());
			dispatch(
				MessageActions.showMessage({
					messages: [
						{
							content: i18n.t('login_Scenario_0001'),
						},
					],
					title: i18n.t('login_Scenario_0002'),
				})
			);
		});
};

/**
 * ユーザ情報読み込み
 * @param dispatch ディスパッチャ
 * @param loginID ログインID
 * @param managerChk 管理者チェックフラグ
 */
export const getUserInfo = (
	dispatch: Dispatch,
	loginID: string,
	managerChk: string
) => {
	// 組織一覧取得
	dispatch(hideProgress());
	Transfers.getUserInfoOrgList()
		.then(data => {
			if (!data || !data.orgList) {
				throw new Error();
			}
			return Transfers.getUserInfoOrgInfo(data.orgList[0].orgId);
		})
		.then(() => {
			return Transfers.getLoginUserInfoModel();
		})
		.then(data => {
			dispatch(SessionActions.setSessionLoginID({ loginID }));
			dispatch(
				SessionActions.setSessionToken({ token: data.secureToken })
			);
			const userAttr: SessionActions.IUserAttr = {
				authorityList: data.authorityList,
				lang: data.langCd,
				locale: data.locale,
				loginType: '',
				managerChk,
				userFamilyName: data.userFamilyName,
				userId: data.userId,
				userName: data.userName,
			};
			dispatch(SessionActions.setSessionUserAttr({ userAttr }));
			dispatch(hideProgress());
			const to = Routes.getPath(Screens.PERSONAL_PORTAL);
			dispatch(push(to));

			// マスタデータを取得しLocalStorageに保持
			// tslint:disable-next-line:prefer-const
			let MULTI_TENANT_MODE: string | null;
			if (Config.multiTenant) {
				MULTI_TENANT_MODE = '1';
			} else {
				MULTI_TENANT_MODE = '0';
			}
			fetchSystemMst(
				MasterItemCode.CAREER_TYPE_CONTROL,
				MULTI_TENANT_MODE,
				data.secureToken
			);
			fetchSystemMst(
				MasterItemCode.USE_JINJI_ZOKUSEI,
				'',
				data.secureToken
			);

			// CMオプション 取得ANDスキルマップ の表示制御取得
			fetchCMData(data.secureToken);

			fetchSystemMst(
				MasterItemCode.QM_APPLICATION_CHECK_NUMBER,
				MULTI_TENANT_MODE,
				data.secureToken
			);

			fetchSystemMst(
				MasterItemCode.QM_CAREER_NUMBER,
				MULTI_TENANT_MODE,
				data.secureToken
			);

			// ログイン後メッセージ取得
			fetchLoggedInMessage(dispatch, data.secureToken);
		})
		.catch(e => {
			// handle network error
			dispatch(hideProgress());
		});
};

/**
 * ログアウト
 * @param dispatch ディスパッチャ
 */
export const logout = (dispatch: Dispatch) => {
	dispatch(showProgress());
	fetch(Config.tsqPath + '/tsq-api/logout.action', {
		credentials: 'include',
		method: 'POST',
	})
		.then(response => {
			dispatch(hideProgress());
			dispatch(RootActions.initialize());
			dispatch(push(Routes.getPath(Screens.LOGIN)));
		})
		.catch(() => {
			dispatch(hideProgress());
		});
};

/**
 * パスワード変更
 * @param dispatch ディスパッチャ
 * @param password パスワード
 */
export const changePassword = (dispatch: Dispatch, password: string) => {
	dispatch(showProgress());
	fetch(Config.tsqPath + '/tsq/ui/auth/changePassword.action', {
		body: JSON.stringify({
			changedPassword: password,
		}),
		credentials: 'include',
		headers: {
			'Content-Type': 'application/json',
		},
		method: 'POST',
	})
		.then(response => {
			if (!response.ok) {
				throw new Error();
			}
			return response.json();
		})
		.then(response => {
			dispatch(hideProgress());
			const data = response.data;
			if (!data.succeededChangePassword.value) {
				// エラー時
				if (
					!data.passwordChangeResultDialogTitle.value ||
					!data.passwordChangeResultDialogMessage.value
				) {
					dispatch(
						MessageActions.showMessage({
							messages: [
								{
									content: 'COM_F_0001',
								},
							],
							title: 'COM_D_0001',
						})
					);
				} else {
					dispatch(
						MessageActions.showMessage({
							messages: [
								{
									content:
										data.passwordChangeResultDialogMessage
											.value,
								},
							],
							title: data.passwordChangeResultDialogTitle.value,
						})
					);
				}
			} else {
				// 成功時
				dispatch(
					LoginActions.setPasswordChangeSuccess({
						passwordChangeSuccess: true,
					})
				);
				dispatch(
					MessageActions.showMessage({
						messages: [
							{
								content:
									data.passwordChangeResultDialogMessage
										.value,
							},
						],
						title: data.passwordChangeResultDialogTitle.value,
					})
				);
			}
		})
		.catch(() => {
			dispatch(hideProgress());
		});
};

/**
 * パスワードリマインダのパスワード変更登録処理
 * @param dispatch ディスパッチャ
 * @param companyCode 法人コード
 * @param loginID ログインID(パスワード再発行)
 * @param email メールアドレス(ログインID通知およびパスワード再発行)
 * @param type 選択する法人確認項目
 * @param inputValue 選択する法人確認項目を対応する入力値
 */
export const pwrSendMail = (
	dispatch: Dispatch,
	email: string,
	inputValue: string,
	loginID: string,
	type: string,
	companyCode?: string
) => {
	dispatch(showProgress());
	Transfers.fetchSendMail(email, inputValue, loginID, type, companyCode)
		.then(_ => {
			// 成功の場合、メール送信完了画面へ遷移する
			dispatch(LoginActions.setSendmailEndDialog({ open: true }));
		})
		.catch((e: ITsqErrorResponse) => {
			if (e.messageKey === 'LMCM_E_00714') {
				dispatch(
					commonHandleShowErrorMessage({
						content: e.messageKey,
						replacements: [],
						transitionTo: Routes.getPath(Screens.LOGIN),
					})
				);
			} else {
				dispatch(
					commonHandleShowErrorMessage({
						content: e.messageKey,
						replacements: [],
					})
				);
			}
		})
		.finally(() => {
			dispatch(hideProgress());
		});
};

/**
 * パスワードリマインダのパスワード変更登録処理
 * @param dispatch ディスパッチャ
 * @param authKey 認証キー
 * @param changedPassword 変更後パスワード
 * @param companyCode 法人コード
 */
export const pwrPasswordChange = (
	dispatch: Dispatch,
	authKey: string,
	changedPassword: string,
	companyCode?: string
) => {
	dispatch(showProgress());
	Transfers.fetchChangePassword(authKey, changedPassword, companyCode)
		.then(_ => {
			// 成功の場合、パスワード変更完了画面へ遷移する
			const to = Routes.getPath(Screens.PWR_CHANGEPASSWORDEND);
			dispatch(push(to));
		})
		.catch((e: ITsqErrorResponse) => {
			if (e.messageKey === 'LMCM_E_00714') {
				dispatch(
					commonHandleShowErrorMessage({
						content: e.messageKey,
						replacements: [],
						transitionTo: Routes.getPath(Screens.LOGIN),
					})
				);
			} else {
				// エラー時
				if (!e.resultDialogTitle || !e.resultDialogMessage) {
					dispatch(
						MessageActions.showMessage({
							messages: [
								{
									content: 'COM_F_0001',
								},
							],
							title: 'COM_D_0001',
						})
					);
				} else if (e.resultDialogTitle && e.resultDialogMessage) {
					dispatch(
						MessageActions.showMessage({
							messages: [
								{
									content: e.resultDialogMessage,
								},
							],
							title: e.resultDialogTitle,
						})
					);
				} else {
					dispatch(
						commonHandleShowErrorMessage({
							content: e.messageKey,
							replacements: [],
						})
					);
				}
			}
		})
		.finally(() => {
			dispatch(hideProgress());
		});
};

/**
 * ログイン後パスワード変更
 * @param dispatch ディスパッチャ
 * @param loginID ログインID
 * @param managerChk 管理者チェックフラグ
 */
export const loginAfterChangePassword = (
	dispatch: Dispatch,
	loginID: string,
	managerChk: string
) => {
	// デスクトップ表示アクション実行
	fetch(Config.tsqPath + '/tsq-api/dispDesktop.action', {
		credentials: 'include',
		method: 'POST',
	}).then(response => {
		// ユーザデータを取得し、お知らせ画面に遷移
		getUserInfo(dispatch, loginID, managerChk);
	});
};

/**
 * ログイン後メッセージ取得シナリオ
 * @param dispatch Dispatchオブジェクト
 * @param token トークン
 */
export const fetchLoggedInMessage = (dispatch: Dispatch, token: string) => {
	const modelId = 'tsq_desktop_initModel';
	Transfers.read(modelId, token)
		.then(response => response.json())
		.then(receiveData => {
			if (receiveData && receiveData[modelId]) {
				const data = receiveData.tsq_desktop_initModel;
				dispatch(
					LoginActions.setLoggedInMessage({
						displayContentsLicenseInfoMessage:
							data.ctrlInfo.displayContentsLicenseInfoMessage,
						displayDesktopMessageDialog:
							data.ctrlInfo.displayDesktopMessageDialog,
						displayLastLoginIPAddress:
							data.ctrlInfo.displayLastLoginIPAddress,
						displayLastLoginInfo:
							data.ctrlInfo.displayLastLoginInfo,
						displaySecuritySamePwdWarningMessage:
							data.ctrlInfo.displaySecuritySamePwdWarningMessage,
						lastCreatedFaildContentIds:
							data.dispInfo.lastCreatedFaildContentIds,
						lastLoginFailedDateTime:
							data.dispInfo.lastLoginFailedDateTime,
						lastLoginFailedIPAddress:
							data.dispInfo.lastLoginFailedIPAddress,
						lastLoginSuccessDateTime:
							data.dispInfo.lastLoginSuccessDateTime,
						lastLoginSuccessIPAddress:
							data.dispInfo.lastLoginSuccessIPAddress,
						remainChangePwdExpireDateNum:
							data.dispInfo.remainChangePwdExpireDateNum,
					})
				);
			}
		});
};



// WEBPACK FOOTER //
// ./src/scenario/login.ts